// ─────────────────────────────────────────────────────────────────────────
// AdminApplicationReview.js
//
// 5-step hospital application review wizard.
// Replaces the old "two-button approve/reject" modal with a structured KYC
// review process:
//
//   1. Overview          → applicant identity snapshot
//   2. Basic Info        → name / address / type / beds / OP timing
//   3. Documents         → inline preview + verify / flag per doc
//   4. Legal & KYC       → reg-no / GST / PAN cross-checks
//   5. Bank Details      → bank info + cancelled cheque preview
//   6. Decision          → Approve · Request Resubmission · Reject
//
// All flags raised by the admin during steps 2-5 are aggregated and either
// shipped as `requestedFixes[]` to the hospital (Resubmission) or attached
// as `adminNotes` (Approve / Reject).
// ─────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect } from 'react';
import {
  MdClose, MdCheckCircle, MdFlag, MdArrowBack, MdArrowForward,
  MdLocalHospital, MdDescription, MdVerified, MdAccountBalance, MdGavel,
  MdPictureAsPdf, MdImage, MdOpenInNew, MdWarning, MdInfo, MdEmail,
  MdFingerprint,
} from 'react-icons/md';
import { adminApplicationsAPI } from '../../services/api';
import { getAdminReviewerName, setAdminReviewerName, ensureAdminReviewerName } from '../../utils/adminReviewer';

// ────────── Constants ────────────────────────────────────────────────
const STEPS = [
  { id: 'overview',  label: 'Overview',     icon: <MdInfo /> },
  { id: 'basic',     label: 'Basic Info',   icon: <MdLocalHospital /> },
  { id: 'docs',      label: 'Documents',    icon: <MdDescription /> },
  { id: 'legal',     label: 'Legal & KYC',  icon: <MdGavel /> },
  { id: 'bank',      label: 'Bank Details', icon: <MdAccountBalance /> },
  { id: 'decision',  label: 'Decision',     icon: <MdVerified /> },
];

// Friendly labels for documentUrls keys.
const DOC_LABELS = {
  registrationCertificate: 'Hospital Registration Certificate',
  tradeLicense:           'Trade License / Business Registration',
  gstCertificate:         'GST Certificate',
  panCard:                'PAN Card',
  moa:                    'MOA — Memorandum of Association',
  aoa:                    'AOA — Articles of Association',
  medicalLicense:         'Medical Establishment License',
  fireSafety:             'Fire Safety Certificate / NOC',
  biomedicalWaste:        'Biomedical Waste Authorization',
  pollutionControl:       'Pollution Control Board Clearance',
  drugLicense:            'Drug License',
  authorizedSignatoryID: 'Authorised Signatory ID',
  buildingNOC:            'Building NOC',
  accreditation:          'NABH / JCI Accreditation',
  cancelledCheque:        'Cancelled Cheque',
  bankProof:              'Bank Account Proof',
  hospitalLogo:           'Hospital Logo',
  signedAgreement:        'Signed Partnership Agreement (PDF)',
};

const isPdf = (url='') => /\.pdf(\?|#|$)/i.test(url);

function agreementSignedPdfUrl(app) {
  const es = app?.kyc?.esign || {};
  return es.signedDocumentUrl || app?.documentUrls?.signedAgreement || null;
}

function agreementAdminStatus(app) {
  const es = app?.kyc?.esign || {};
  const pdf = agreementSignedPdfUrl(app);
  if (pdf && (es.status === 'signed' || es.signedDocumentUrl)) {
    return { key: 'complete', label: 'Signed & uploaded', color: '#5eead4', bg: 'rgba(0,212,170,0.15)' };
  }
  if (pdf) {
    return { key: 'pdf', label: 'PDF on file', color: '#5eead4', bg: 'rgba(0,212,170,0.15)' };
  }
  if (es.status === 'signed_pending_upload') {
    return { key: 'awaiting_pdf', label: 'Awaiting signed PDF', color: '#fbbf24', bg: 'rgba(245,158,11,0.15)' };
  }
  if (es.status === 'pending' || es.status === 'initiating' || es.invitationLink || es.transactionId || es.docketId) {
    return { key: 'esign_started', label: 'eSign started', color: '#fbbf24', bg: 'rgba(245,158,11,0.15)' };
  }
  return { key: 'missing', label: 'Not completed', color: '#fca5a5', bg: 'rgba(239,68,68,0.12)' };
}

function signedViaLabel(es) {
  if (es?.signedVia === 'esign_and_upload') return 'Aadhaar eSign + PDF upload';
  if (es?.signedVia === 'manual_upload') return 'Signed PDF upload';
  if (es?.initiatedAt || es?.charged) return 'eSign session (check PDF)';
  return '—';
}

function fmtAdminDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN'); } catch (_) { return iso; }
}

// ────────── Reusable bits ─────────────────────────────────────────────
const Pill = ({ children, color='#94a3b8', bg='rgba(148,163,184,0.12)' }) => (
  <span style={{
    display:'inline-flex', alignItems:'center', gap:4,
    padding:'2px 10px', borderRadius:999, background:bg, color,
    fontSize:11, fontWeight:700, letterSpacing:0.4,
  }}>{children}</span>
);

function FieldCard({ label, value, fieldKey, flags, onFlag, onUnflag, mono=false }) {
  const flag = flags.find(f => f.field === fieldKey);
  const flagged = !!flag;
  return (
    <div style={{
      padding:14, borderRadius:10,
      background: flagged ? 'rgba(239,68,68,0.06)' : 'var(--surface-subtle)',
      border: `1px solid ${flagged ? 'rgba(239,68,68,0.3)' : 'var(--surface-muted)'}`,
      transition:'all 0.2s',
    }}>
      <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
          <div style={{
            fontSize:14, fontWeight:600,
            wordBreak:'break-word',
            fontFamily: mono ? 'monospace' : 'inherit',
            color: value ? '#e2e8f0' : 'var(--text-muted)',
          }}>{value || '— Not provided —'}</div>
          {flagged && (
            <div style={{ fontSize:12, color:'#fca5a5', marginTop:6, fontStyle:'italic' }}>
              ⚠️ {flag.issue}
            </div>
          )}
        </div>
        {flagged ? (
          <button
            onClick={() => onUnflag(fieldKey)}
            title="Remove flag"
            style={{
              padding:'4px 10px', background:'rgba(239,68,68,0.15)',
              border:'1px solid rgba(239,68,68,0.35)', borderRadius:6,
              color:'#fca5a5', cursor:'pointer', fontSize:11, fontWeight:700,
              whiteSpace:'nowrap',
            }}
          >Unflag</button>
        ) : (
          <button
            onClick={() => onFlag(fieldKey, label)}
            title="Flag this field as incorrect / missing"
            style={{
              padding:'4px 10px', background:'rgba(245,158,11,0.1)',
              border:'1px solid rgba(245,158,11,0.25)', borderRadius:6,
              color:'#fbbf24', cursor:'pointer', fontSize:11, fontWeight:700,
              display:'flex', alignItems:'center', gap:3, whiteSpace:'nowrap',
            }}
          ><MdFlag size={13}/>Flag</button>
        )}
      </div>
    </div>
  );
}

// ── KYC registry display (matches hospital application UI) ─────────────────
function adminKycPrimaryName(kind, details) {
  const d = details || {};
  if (kind === 'gst') return d.legalName || d.businessName || '';
  if (kind === 'pan') return d.registeredName || '';
  if (kind === 'cin') return d.companyName || '';
  if (kind === 'bank') return d.nameAtBank || '';
  return '';
}

function adminKycDetailRows(kind, result) {
  const d = result?.details || {};
  const rows = [];
  const push = (label, value) => {
    if (value != null && String(value).trim() !== '') rows.push({ label, value: String(value).trim() });
  };

  if (kind === 'gst') {
    push('Legal name', d.legalName);
    if (d.businessName && d.businessName !== d.legalName) push('Trade name', d.businessName);
    push('GST status', d.gstStatus);
    push('Taxpayer type', d.taxpayerType);
    push('Constitution', d.constitution);
    push('Registered address', d.address);
  } else if (kind === 'pan') {
    push('Name on PAN', d.registeredName);
    push('PAN status', d.panStatus);
    push('Category', d.panType);
    if (d.dob) push('Date of birth', d.dob);
    if (d.nameMatchScore != null) push('Name match', `${d.nameMatchScore}%`);
  } else if (kind === 'cin') {
    push('Company name', d.companyName);
    push('MCA status', d.companyStatus);
    if (d.mcaStatus && d.mcaStatus !== d.companyStatus) push('Lookup status', d.mcaStatus);
    push('Company type', d.companyType);
    push('ROC', d.rocCode);
    push('Registration no.', d.registrationNumber);
    push('Incorporated', d.incorporationDate);
    push('Registered address', d.registeredAddress);
    if (Array.isArray(d.directors) && d.directors.length) push('Directors', d.directors.join(', '));
  } else if (kind === 'bank') {
    push('Account holder', d.nameAtBank);
    push('Verification', d.verificationMethod === 'penny_drop' ? 'Bank penny drop (NPCI)' : null);
    push('Account status', d.accountStatus);
    if (d.nameMatchScore != null) push('Name match', `${d.nameMatchScore}%`);
  }
  return rows;
}

function adminKycHasData(kind, result) {
  if (!result || typeof result !== 'object') return false;
  if (result.verified === true || result.status === 'partial' || result.status === 'verified') return true;
  return Boolean(adminKycPrimaryName(kind, result.details));
}

function adminKycStatus(result, kind) {
  if (!result) return { code: 'not_run', label: 'Not checked', color: '#fbbf24', bg: 'rgba(245,158,11,0.05)', border: 'rgba(245,158,11,0.25)' };
  if (result.status === 'pending') {
    return { code: 'pending', label: 'In progress', color: '#93c5fd', bg: 'rgba(59,130,246,0.06)', border: 'rgba(59,130,246,0.25)' };
  }
  if (result.verified === true) {
    return { code: 'confirmed', label: 'Confirmed', color: '#5eead4', bg: 'rgba(0,212,170,0.06)', border: 'rgba(0,212,170,0.3)' };
  }
  if (adminKycHasData(kind, result)) {
    return { code: 'partial', label: 'Record found', color: '#93c5fd', bg: 'rgba(59,130,246,0.06)', border: 'rgba(59,130,246,0.25)' };
  }
  if (result.status === 'format_verified' || result.status === 'skipped') {
    return { code: 'format', label: 'Format only', color: '#fbbf24', bg: 'rgba(245,158,11,0.05)', border: 'rgba(245,158,11,0.25)' };
  }
  return { code: 'failed', label: 'Could not confirm', color: '#fca5a5', bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.3)' };
}

function KycPanel({ kyc, only /* ['gst','pan'] | ['bank'] | undefined for all */ }) {
  const k = kyc || {};
  const all = [
    { key: 'gst',  label: 'GST verification',  res: k.gst,  hint: 'Checks GSTIN against government GST records.' },
    { key: 'pan',  label: 'PAN verification',  res: k.pan,  hint: 'Checks PAN against income-tax records.' },
    { key: 'bank', label: 'Bank account verification', res: k.bank, hint: 'Confirms account holder name with the beneficiary bank.' },
    { key: 'cin',  label: 'CIN / MCA verification', res: k.cin,  hint: 'Checks company registration against MCA records.' },
  ];
  const rows = only ? all.filter(r => only.indexOf(r.key) >= 0) : all;

  return (
    <div style={{ display:'grid', gap:12 }}>
      <div>
        <div style={{ fontWeight:800, fontSize:14, color:'var(--text-primary)', marginBottom:4 }}>
          Verified business details
        </div>
        <div style={{ fontSize:12, color:'var(--text-muted)', lineHeight:1.5 }}>
          Pulled automatically from government and banking records during the application.
        </div>
      </div>
      {rows.map(({ key, label, res, hint }) => {
        const status = adminKycStatus(res, key);
        const name = adminKycPrimaryName(key, res?.details);
        const detailRows = adminKycDetailRows(key, res);
        const hasData = adminKycHasData(key, res);
        return (
          <div key={key} style={{ padding:14, borderRadius:12, background:status.bg, border:`1px solid ${status.border}` }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, marginBottom:8, flexWrap:'wrap' }}>
              <div style={{ fontWeight:700, fontSize:13, color:'var(--text-primary)' }}>{label}</div>
              <div style={{ fontSize:11, fontWeight:800, color:status.color, padding:'3px 10px', borderRadius:999, background:'rgba(0,0,0,0.2)', letterSpacing:'0.04em' }}>
                {status.label.toUpperCase()}
              </div>
            </div>

            {hasData && (
              <>
                <div style={{ fontSize:11.5, color:status.color, fontWeight:700, marginBottom:6, display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ width:7, height:7, borderRadius:'50%', background:status.color, display:'inline-block' }} />
                  {status.code === 'confirmed'
                    ? (key === 'bank' ? 'Confirmed with bank records' : 'Confirmed with government records')
                    : 'Record found — review details below'}
                </div>
                {name && <div style={{ fontSize:14, fontWeight:800, color:'var(--text-primary)', marginBottom:8 }}>{name}</div>}
                {detailRows.length > 0 && (
                  <div style={{ display:'grid', gap:4, fontSize:12, color:'var(--text-secondary)', lineHeight:1.65 }}>
                    {detailRows.map((row) => (
                      <div key={row.label}><strong>{row.label}:</strong> {row.value}</div>
                    ))}
                  </div>
                )}
                {res?.verifiedAt && (
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:8 }}>
                    Checked at: {new Date(res.verifiedAt).toLocaleString('en-IN')}
                  </div>
                )}
              </>
            )}

            {!hasData && status.code === 'failed' && (
              <div style={{ fontSize:12, color:'#fca5a5', lineHeight:1.6 }}>
                {res?.error || res?.lookupNote || res?.status || 'Verification could not be completed'}
              </div>
            )}

            {!hasData && status.code === 'format' && (
              <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>
                Number format is valid, but live registry lookup was not available when this application was saved. {hint}
              </div>
            )}

            {!hasData && status.code === 'not_run' && (
              <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>
                No live registry result was saved for this field. {hint}
              </div>
            )}

            {!hasData && status.code === 'pending' && (
              <div style={{ fontSize:12, color:'var(--text-secondary)', lineHeight:1.6 }}>
                {res?.lookupNote || 'Registry lookup is still in progress.'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function EsignAgreementPanel({ app, flags, onFlag, onUnflag }) {
  const es = app.kyc?.esign || {};
  const pdfUrl = agreementSignedPdfUrl(app);
  const status = agreementAdminStatus(app);
  const flag = flags.find(f => f.field === 'esign.agreement' || f.field === 'doc:signedAgreement');
  const flagged = !!flag;

  return (
    <div style={{
      padding:16, borderRadius:12, marginBottom:4,
      background: flagged ? 'rgba(239,68,68,0.06)' : 'rgba(0,212,170,0.05)',
      border: `1px solid ${flagged ? 'rgba(239,68,68,0.3)' : 'rgba(0,212,170,0.28)'}`,
    }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:12, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <MdFingerprint style={{ fontSize:22, color:'#00d4aa' }} />
          <div>
            <div style={{ fontWeight:800, fontSize:14, color:'var(--text-primary)' }}>Partnership agreement &amp; eSign</div>
            <div style={{ fontSize:11.5, color:'var(--text-muted)', marginTop:2 }}>Signed PDF + optional Aadhaar eSign session</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <Pill color={status.color} bg={status.bg}>{status.label}</Pill>
          {flagged ? (
            <button type="button" onClick={() => onUnflag(flag.field)} style={{
              padding:'4px 10px', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.35)',
              borderRadius:6, color:'#fca5a5', cursor:'pointer', fontSize:11, fontWeight:700,
            }}>Unflag</button>
          ) : (
            <button type="button" onClick={() => onFlag('esign.agreement', 'Partnership agreement')} style={{
              padding:'4px 10px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.25)',
              borderRadius:6, color:'#fbbf24', cursor:'pointer', fontSize:11, fontWeight:700,
              display:'flex', alignItems:'center', gap:3,
            }}><MdFlag size={13}/>Flag</button>
          )}
        </div>
      </div>

      {flagged && (
        <div style={{ fontSize:12, color:'#fca5a5', marginBottom:10, fontStyle:'italic' }}>⚠️ {flag.issue}</div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px, 1fr))', gap:10, fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.7 }}>
        <div><strong style={{ color:'var(--text-muted)' }}>eSign status:</strong> {es.status || '—'}</div>
        <div><strong style={{ color:'var(--text-muted)' }}>Completion:</strong> {signedViaLabel(es)}</div>
        <div><strong style={{ color:'var(--text-muted)' }}>Signed at:</strong> {fmtAdminDate(es.signedAt)}</div>
        <div><strong style={{ color:'var(--text-muted)' }}>PDF uploaded:</strong> {fmtAdminDate(es.signedDocumentUploadedAt)}</div>
        <div><strong style={{ color:'var(--text-muted)' }}>Provider:</strong> {es.providerStatus || '—'}</div>
        <div><strong style={{ color:'var(--text-muted)' }}>Signatory:</strong> {app.ownerName || app.formData?.ownerName || '—'}</div>
        <div style={{ gridColumn:'1 / -1' }}>
          <strong style={{ color:'var(--text-muted)' }}>CGPE ref:</strong>{' '}
          {es.transactionId || es.docketId || es.documentId || '—'}
          {es.charged === true && <span style={{ marginLeft:8, color:'#fbbf24' }}>(eSign charged)</span>}
        </div>
      </div>

      {pdfUrl ? (
        <div style={{ marginTop:14, display:'flex', flexWrap:'wrap', gap:10, alignItems:'center' }}>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{
            padding:'8px 14px', borderRadius:8, fontSize:12.5, fontWeight:700,
            background:'rgba(0,212,170,0.15)', border:'1px solid rgba(0,212,170,0.35)',
            color:'#5eead4', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6,
          }}>
            <MdPictureAsPdf /> View signed agreement PDF
          </a>
          {es.invitationLink && (
            <a href={es.invitationLink} target="_blank" rel="noopener noreferrer" style={{
              padding:'8px 14px', borderRadius:8, fontSize:12.5, fontWeight:700,
              background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.25)',
              color:'#a5b4fc', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6,
            }}>
              <MdOpenInNew size={14}/> eSign portal link
            </a>
          )}
        </div>
      ) : (
        <div style={{
          marginTop:14, padding:12, borderRadius:8, fontSize:12.5,
          background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)', color:'#fca5a5',
        }}>
          No signed partnership PDF on file yet. Applicant must upload the signed agreement before approval.
        </div>
      )}
    </div>
  );
}

function DocumentViewer({ docKey, url, flags, onFlag, onUnflag }) {
  const label    = DOC_LABELS[docKey] || docKey.replace(/([A-Z])/g,' $1').replace(/^./, s => s.toUpperCase());
  const flag     = flags.find(f => f.field === `doc:${docKey}`);
  const flagged  = !!flag;
  const isPdfDoc = isPdf(url);

  return (
    <div style={{
      borderRadius:12,
      border:`1px solid ${flagged ? 'rgba(239,68,68,0.3)' : 'var(--border-color)'}`,
      background: flagged ? 'rgba(239,68,68,0.04)' : 'var(--surface-subtle)',
      overflow:'hidden',
    }}>
      {/* Header */}
      <div style={{
        display:'flex', alignItems:'center', gap:10, padding:'10px 14px',
        borderBottom:'1px solid var(--surface-subtle)',
        background:'rgba(0,0,0,0.2)',
      }}>
        <span style={{ fontSize:18 }}>
          {isPdfDoc ? <MdPictureAsPdf style={{ color:'#ef4444' }}/> : <MdImage style={{ color:'#6366f1' }}/>}
        </span>
        <div style={{ flex:1, fontWeight:700, fontSize:13 }}>{label}</div>
        <a href={url} target="_blank" rel="noreferrer"
          style={{
            padding:'4px 10px', background:'rgba(99,102,241,0.15)',
            border:'1px solid rgba(99,102,241,0.3)', borderRadius:6,
            color:'#a5b4fc', fontSize:11, fontWeight:700,
            display:'inline-flex', alignItems:'center', gap:3, textDecoration:'none',
          }}>
          <MdOpenInNew size={12}/> Open
        </a>
        {flagged ? (
          <button onClick={() => onUnflag(`doc:${docKey}`)}
            style={{
              padding:'4px 10px', background:'rgba(239,68,68,0.15)',
              border:'1px solid rgba(239,68,68,0.3)', borderRadius:6,
              color:'#fca5a5', cursor:'pointer', fontSize:11, fontWeight:700,
            }}>Unflag</button>
        ) : (
          <button onClick={() => onFlag(`doc:${docKey}`, label)}
            style={{
              padding:'4px 10px', background:'rgba(245,158,11,0.1)',
              border:'1px solid rgba(245,158,11,0.25)', borderRadius:6,
              color:'#fbbf24', cursor:'pointer', fontSize:11, fontWeight:700,
              display:'inline-flex', alignItems:'center', gap:3,
            }}><MdFlag size={12}/>Flag</button>
        )}
      </div>

      {/* Inline preview */}
      <div style={{ background:'#0a1020', minHeight:280, maxHeight:480 }}>
        {isPdfDoc ? (
          <iframe
            src={url}
            title={label}
            style={{ width:'100%', height:480, border:'none', display:'block' }}
          />
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:14 }}>
            <img
              src={url}
              alt={label}
              style={{ maxWidth:'100%', maxHeight:440, borderRadius:8, objectFit:'contain' }}
              onError={(e)=>{ e.target.style.display='none'; e.target.parentNode.innerHTML='<div style="color:#94a3b8;font-size:13;padding:40px">⚠ Could not load preview. Click "Open" to view.</div>'; }}
            />
          </div>
        )}
      </div>

      {flagged && (
        <div style={{
          padding:'8px 14px', borderTop:'1px solid rgba(239,68,68,0.2)',
          background:'rgba(239,68,68,0.08)', color:'#fca5a5', fontSize:12, fontStyle:'italic',
        }}>⚠️ {flag.issue}</div>
      )}
    </div>
  );
}

// ────────── Issue prompt (collects an issue note when flagging) ────────
function FlagDialog({ field, label, onCancel, onSubmit }) {
  const [text, setText] = useState('');
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200,
      display:'flex', alignItems:'center', justifyContent:'center', padding:20,
    }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{
        background:'#0d1525', border:'1px solid rgba(245,158,11,0.3)',
        borderRadius:12, padding:22, width:'100%', maxWidth:460,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
          <MdFlag style={{ color:'#fbbf24', fontSize:22 }}/>
          <div style={{ fontWeight:800, fontSize:15 }}>Flag: {label}</div>
        </div>
        <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:14 }}>
          Describe what's wrong or what the hospital must correct. Your name must be
          entered in the reviewer bar above — it is recorded permanently with this flag.
        </div>
        <textarea
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          rows={3}
          placeholder="e.g. PAN number doesn't match the PAN card upload"
          style={{
            width:'100%', padding:10, fontSize:13,
            background:'var(--surface-subtle)', border:'1px solid var(--border-color-hover)',
            borderRadius:8, color:'var(--text-primary)', resize:'vertical', fontFamily:'inherit',
          }}
        />
        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:14 }}>
          <button onClick={onCancel} style={{
            padding:'8px 16px', background:'transparent',
            border:'1px solid var(--surface-strong)', borderRadius:8,
            color:'var(--text-secondary)', cursor:'pointer', fontSize:13,
          }}>Cancel</button>
          <button
            disabled={!text.trim()}
            onClick={() => onSubmit(text.trim())}
            style={{
              padding:'8px 18px',
              background: text.trim() ? 'rgba(245,158,11,0.25)' : 'rgba(245,158,11,0.08)',
              border:'1px solid rgba(245,158,11,0.4)', borderRadius:8,
              color:'#fbbf24', cursor: text.trim() ? 'pointer' : 'not-allowed',
              fontSize:13, fontWeight:700,
            }}>Add Flag</button>
        </div>
      </div>
    </div>
  );
}

// ────────── Main wizard component ──────────────────────────────────────
// PRODUCTION FIX (May 25, 2026): the admin Lambda persists requestedFixes as
// a JSON.stringify'd string in DynamoDB. Without this normaliser, the very
// first render of this component crashes with
//   TypeError: app.requestedFixes.filter is not a function
// the moment the admin clicks "View Details" on any row whose status is
// `needs_resubmission` (and on any future row with persisted flags).
function parseReviewHistory(app) {
  const h = app?.reviewHistory;
  if (Array.isArray(h)) return h;
  if (typeof h === 'string' && h.trim()) {
    try {
      const p = JSON.parse(h);
      return Array.isArray(p) ? p : [];
    } catch (_) { return []; }
  }
  return [];
}

function reviewActionLabel(action) {
  if (action === 'approved') return 'Approved';
  if (action === 'rejected') return 'Rejected';
  if (action === 'needs_resubmission') return 'Sent back for fixes';
  return action || 'Review';
}

function _normalizeFixes(rf) {
  if (Array.isArray(rf)) return rf;
  if (typeof rf === 'string' && rf.trim()) {
    try {
      const parsed = JSON.parse(rf);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) { return []; }
  }
  return [];
}

export default function AdminApplicationReview({ app, onClose, onUpdated, toast }) {
  const [step, setStep]         = useState(0);
  const [liveApp, setLiveApp]   = useState(app);
  const [flags, setFlags]       = useState(() => _normalizeFixes(app.requestedFixes));
  const [pendFlag, setPendFlag] = useState(null);     // { field, label }
  const [decision, setDecision] = useState('');       // 'approve' | 'resubmit' | 'reject' | ''
  const [decNote, setDecNote]   = useState('');
  const [submitting, setSub]    = useState(false);
  const [credentials, setCredentials] = useState(null); // { hospitalId, email, tempPassword }
  const [reviewerName, setReviewerName] = useState(() => getAdminReviewerName());

  const reviewHistory = useMemo(() => parseReviewHistory(liveApp), [liveApp]);

  useEffect(() => {
    setLiveApp(app);
    setFlags(_normalizeFixes(app.requestedFixes));
  }, [app]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await adminApplicationsAPI.getById(app.applicationId);
      if (!cancelled && r.data) setLiveApp(r.data);
    })();
    return () => { cancelled = true; };
  }, [app.applicationId]);

  const docs = useMemo(() => {
    const u = liveApp.documentUrls || {};
    const entries = Object.entries(u).filter(([_, v]) => v);
    entries.sort((a, b) => {
      if (a[0] === 'signedAgreement') return -1;
      if (b[0] === 'signedAgreement') return 1;
      return (DOC_LABELS[a[0]] || a[0]).localeCompare(DOC_LABELS[b[0]] || b[0]);
    });
    return entries;
  }, [liveApp]);

  const complianceDocs = useMemo(() => docs.filter(([k]) => k !== 'signedAgreement'), [docs]);
  const agreementDoc = useMemo(() => docs.find(([k]) => k === 'signedAgreement'), [docs]);
  const agreementStatus = useMemo(() => agreementAdminStatus(liveApp), [liveApp]);

  const flagsBySection = useMemo(() => ({
    basic:  flags.filter(f => f.section === 'basic'),
    docs:   flags.filter(f => f.section === 'docs'),
    legal:  flags.filter(f => f.section === 'legal'),
    bank:   flags.filter(f => f.section === 'bank'),
  }), [flags]);

  // ── Flag handlers ────────────────────────────────────────────────────
  const requestFlag = (field, label) => setPendFlag({ field, label });
  const confirmFlag = (issue) => {
    const name = ensureAdminReviewerName(reviewerName, toast);
    if (!name) return;
    setReviewerName(name);
    const sectionMap = { basic:'basic', legal:'legal', bank:'bank' };
    const section =
      pendFlag.field.startsWith('doc:') ? 'docs'
      : (pendFlag.field.startsWith('esign.') || pendFlag.field === 'signedAgreement') ? 'legal'
      : sectionMap[STEPS[step].id] || STEPS[step].id;
    setFlags(prev => [...prev, {
      section,
      field: pendFlag.field,
      label: pendFlag.label,
      issue,
      flaggedByName: name,
      flaggedAt: new Date().toISOString(),
    }]);
    setPendFlag(null);
  };
  const removeFlag = (field) =>
    setFlags(prev => prev.filter(f => f.field !== field));

  // ── Submit decisions ─────────────────────────────────────────────────
  const submit = async (status) => {
    const name = ensureAdminReviewerName(reviewerName, toast);
    if (!name) return;
    setReviewerName(name);
    if (status === 'rejected' && !decNote.trim()) {
      toast?.('Please provide a rejection reason', true); return;
    }
    if (status === 'needs_resubmission' && flags.length === 0) {
      toast?.('Add at least one flag before requesting resubmission', true); return;
    }
    setSub(true);
    const r = await adminApplicationsAPI.update(liveApp.applicationId, {
      status,
      rejectionReason: status === 'rejected' ? decNote.trim() : undefined,
      requestedFixes:  status === 'needs_resubmission' ? flags : undefined,
      notes:           decNote.trim(),
      reviewerName:    name,
    });
    setSub(false);
    if (r.error) { toast?.(r.error, true); return; }
    if (status === 'approved') {
      const hid = r.data?.hospitalId || '(allotted)';
      toast?.(`✅ Approved · Hospital ID ${hid}. Login credentials emailed to ${liveApp.email}.`);
      if (r.data?.tempPassword) {
        setCredentials({
          hospitalId: hid,
          email: r.data?.email || liveApp.email,
          tempPassword: r.data.tempPassword,
          cognitoOk: r.data?.cognito === true,
          cognitoWarning: r.data?.cognitoWarning,
        });
        return;
      }
    } else {
      const verb = status === 'rejected' ? 'rejected' : 'sent back to applicant';
      toast?.(`Application ${verb} ✓`);
    }
    onUpdated?.({ ...liveApp, status, hospitalId: r.data?.hospitalId, requestedFixes: flags });
    onClose?.();
  };

  // ── Step bodies ──────────────────────────────────────────────────────
  const renderStep = () => {
    const app = liveApp;
    const sid = STEPS[step].id;

    if (sid === 'overview') {
      return (
        <div>
          <div style={{
            padding:20, borderRadius:12, marginBottom:16,
            background:'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(99,102,241,0.04))',
            border:'1px solid rgba(99,102,241,0.25)',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{
                width:56, height:56, borderRadius:14,
                background:'rgba(99,102,241,0.2)', display:'grid', placeItems:'center',
                fontSize:28,
              }}>🏥</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:20, fontWeight:800, marginBottom:4 }}>{app.hospitalName}</div>
                <div style={{ fontSize:13, color:'var(--text-secondary)' }}>
                  {app.hospitalType || 'Private'} · {app.city}, {app.state}
                </div>
              </div>
              <Pill
                color={app.status==='pending' ? '#fbbf24' : app.status==='approved' ? '#00d4aa' : app.status==='needs_resubmission' ? '#a855f7' : '#f87171'}
                bg={app.status==='pending' ? 'rgba(245,158,11,0.15)' : app.status==='approved' ? 'rgba(0,212,170,0.15)' : app.status==='needs_resubmission' ? 'rgba(168,85,247,0.15)' : 'rgba(239,68,68,0.15)'}
              >{(app.status||'pending').replace('_',' ').toUpperCase()}</Pill>
            </div>
          </div>

          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <FieldCard label="Application ID" value={app.applicationId} fieldKey="applicationId" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
            <FieldCard label="Submitted" value={app.submittedAt ? new Date(app.submittedAt).toLocaleString('en-IN') : '—'} fieldKey="submittedAt" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Last reviewed by" value={app.reviewedByName || '—'} fieldKey="reviewedByName" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Contact Person" value={app.contactName} fieldKey="contactName" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Email" value={app.email} fieldKey="email" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Phone" value={app.phone} fieldKey="phone" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Emergency Phone" value={app.emergencyPhone} fieldKey="emergencyPhone" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Website" value={app.website || app.formData?.website} fieldKey="website" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>

          {/* ── Authorized Person ─────────────────────────────────── */}
          <div style={{
            padding:14, borderRadius:10, marginBottom:16,
            background:'rgba(99,102,241,0.05)', border:'1px solid rgba(99,102,241,0.18)',
          }}>
            <div style={{ fontWeight:700, color:'#a5b4fc', fontSize:13, marginBottom:10 }}>
              👤 Authorized Person / Hospital Owner
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              <FieldCard label="Owner Name"  value={app.ownerName        || app.formData?.ownerName}        fieldKey="ownerName"        flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
              <FieldCard label="Designation" value={app.ownerDesignation || app.formData?.ownerDesignation} fieldKey="ownerDesignation" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
              <FieldCard label="Owner Phone" value={app.ownerPhone       || app.formData?.ownerPhone}       fieldKey="ownerPhone"       flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            </div>
            <div style={{ marginTop:10, fontSize:11.5, color:'var(--text-muted)' }}>
              Cross-check the name above against the PAN card and the Hospital Registration
              Certificate documents on Step 3.
            </div>
          </div>

          <div style={{
            padding:14, borderRadius:10, marginBottom:16,
            background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.22)',
          }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
              <div style={{ fontWeight:700, color:'#5eead4', fontSize:13, display:'flex', alignItems:'center', gap:8 }}>
                <MdFingerprint /> Partnership agreement
              </div>
              <Pill color={agreementStatus.color} bg={agreementStatus.bg}>{agreementStatus.label}</Pill>
            </div>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:8, lineHeight:1.6 }}>
              {agreementSignedPdfUrl(app)
                ? <>Signed PDF on file · {signedViaLabel(app.kyc?.esign || {})}</>
                : 'Signed partnership PDF not uploaded yet — check Legal & Documents steps before approving.'}
            </div>
          </div>

          {reviewHistory.length > 0 && (
            <div style={{
              padding:14, borderRadius:10, marginBottom:16,
              background:'var(--surface-subtle)', border:'1px solid var(--border-color-hover)',
            }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:10, color:'var(--text-primary)' }}>
                Review history (permanent)
              </div>
              <div style={{ display:'grid', gap:8 }}>
                {reviewHistory.map((entry, i) => (
                  <div key={i} style={{ fontSize:12.5, color:'var(--text-secondary)', lineHeight:1.6 }}>
                    <strong style={{ color:'#a5b4fc' }}>{reviewActionLabel(entry.action)}</strong>
                    {' · '}
                    <strong>{entry.reviewerName || 'Unknown'}</strong>
                    {entry.at && <> · {fmtAdminDate(entry.at)}</>}
                    {entry.flagCount != null && <> · {entry.flagCount} flag(s)</>}
                    {entry.hospitalId && <> · Hospital ID {entry.hospitalId}</>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {app.status === 'needs_resubmission' && flags.length > 0 && (
            <div style={{
              padding:14, borderRadius:10, marginBottom:16,
              background:'rgba(168,85,247,0.08)', border:'1px solid rgba(168,85,247,0.3)',
            }}>
              <div style={{ fontWeight:700, color:'#c4b5fd', marginBottom:8, fontSize:13 }}>
                ⏳ Already sent back to applicant — awaiting resubmission
              </div>
              <ul style={{ margin:0, paddingLeft:20, fontSize:12.5, color:'#cbd5e1' }}>
                {flags.map((f,i) => <li key={i}>{f.label || f.field}: <em style={{ color:'#fde68a' }}>{f.issue}</em></li>)}
              </ul>
            </div>
          )}

          <div style={{
            padding:14, borderRadius:10, background:'rgba(99,102,241,0.05)',
            border:'1px dashed rgba(99,102,241,0.2)', fontSize:13, color:'var(--text-secondary)',
          }}>
            <div style={{ fontWeight:700, color:'#a5b4fc', marginBottom:6 }}>📋 Review process</div>
            Walk through each step. Use <strong>Flag</strong> beside any field or document
            that's incorrect, missing, or unclear. On the final step you can <strong>Approve</strong>,
            <strong> Request Resubmission</strong> (emails the applicant a list of what to fix
            with a resume link), or <strong>Reject</strong>.
          </div>
        </div>
      );
    }

    if (sid === 'basic') {
      // Defensive read — some legacy applications keep these inside formData.
      const fd = app.formData || {};
      const v = (k) => app[k] != null && app[k] !== '' ? app[k] : fd[k];
      const mapsLink = v('latitude') != null && v('longitude') != null
        ? `https://www.google.com/maps?q=${v('latitude')},${v('longitude')}` : null;
      return (
        <div style={{ display:'grid', gap:12 }}>
          <FieldCard label="Hospital Name"  value={v('hospitalName')}  fieldKey="hospitalName"  flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FieldCard label="Hospital Type"  value={v('hospitalType')}  fieldKey="hospitalType"  flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Website"        value={v('website')}       fieldKey="website"       flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>
          <FieldCard label="Address"        value={v('address')}       fieldKey="address"       flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12 }}>
            <FieldCard label="City"     value={v('city')}     fieldKey="city"     flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="District" value={v('district')} fieldKey="district" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="State"    value={v('state')}    fieldKey="state"    flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="Pincode"  value={v('pincode')}  fieldKey="pincode"  flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>
          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FieldCard label="Latitude"  value={v('latitude')}  fieldKey="latitude"  flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
            <FieldCard label="Longitude" value={v('longitude')} fieldKey="longitude" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
          </div>
          {mapsLink && (
            <a href={mapsLink} target="_blank" rel="noreferrer" style={{
              padding:'8px 14px', borderRadius:8, fontSize:12.5, fontWeight:700,
              background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.25)',
              color:'#a5b4fc', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:6, alignSelf:'start',
            }}>
              <MdOpenInNew size={14}/> Verify pinned location on Google Maps
            </a>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <FieldCard label="OP Fee (₹)" value={app.opFee     || (app.formData?.opFee)}     fieldKey="opFee"     flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <FieldCard label="OP Start"   value={app.opStart || app.formData?.opStart} fieldKey="opStart" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="OP End"     value={app.opEnd   || app.formData?.opEnd}   fieldKey="opEnd"   flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="24 × 7"     value={(app.is24x7 || app.formData?.is24x7) ? 'Yes' : 'No'} fieldKey="is24x7" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>
        </div>
      );
    }

    if (sid === 'docs') {
      return (
        <div>
          <EsignAgreementPanel app={app} flags={flags} onFlag={requestFlag} onUnflag={removeFlag} />

          {agreementDoc && (
            <div style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:'#5eead4', marginBottom:10 }}>
                Signed partnership agreement
              </div>
              <DocumentViewer
                docKey={agreementDoc[0]} url={agreementDoc[1]}
                flags={flags} onFlag={requestFlag} onUnflag={removeFlag}
              />
            </div>
          )}

          <div style={{ fontWeight:700, fontSize:13, color:'var(--text-primary)', marginBottom:10 }}>
            Compliance &amp; KYC documents
          </div>

          {complianceDocs.length === 0 && !agreementDoc ? (
            <div style={{
              padding:30, textAlign:'center', borderRadius:12,
              background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.25)',
              color:'#fca5a5',
            }}>
              <MdWarning size={36} style={{ marginBottom:8 }}/>
              <div style={{ fontWeight:700, fontSize:15 }}>No documents uploaded</div>
              <div style={{ fontSize:12.5, marginTop:6, color:'var(--text-secondary)' }}>
                You should typically Reject or Request Resubmission with a "Please upload all required documents" note.
              </div>
            </div>
          ) : complianceDocs.length === 0 ? (
            <div style={{
              padding:16, borderRadius:10, fontSize:12.5, color:'var(--text-secondary)',
              background:'var(--surface-subtle)', border:'1px solid var(--border-color)',
            }}>
              No other compliance documents uploaded.
            </div>
          ) : (
            <div style={{ display:'grid', gap:14 }}>
              {complianceDocs.map(([key, url]) => (
                <DocumentViewer
                  key={key} docKey={key} url={url}
                  flags={flags} onFlag={requestFlag} onUnflag={removeFlag}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    if (sid === 'legal') {
      const fd = app.formData || {};
      const v = (k) => app[k] != null && app[k] !== '' ? app[k] : fd[k];
      const regDate = v('registrationDate');
      return (
        <div style={{ display:'grid', gap:12 }}>
          <EsignAgreementPanel app={app} flags={flags} onFlag={requestFlag} onUnflag={removeFlag} />

          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
            <FieldCard label="Hospital Registration Number" value={v('registrationNumber')} fieldKey="registrationNumber" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
            <FieldCard label="Registration Date" value={regDate ? new Date(regDate).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : ''} fieldKey="registrationDate" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>
          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FieldCard label="GST Number" value={v('gstNumber')} fieldKey="gstNumber" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
            <FieldCard label="PAN Number" value={v('panNumber')} fieldKey="panNumber" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
          </div>
          {v('cinNumber') && (
            <FieldCard label="CIN / MCA Number" value={v('cinNumber')} fieldKey="cinNumber" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
          )}

          <KycPanel kyc={app.kyc} only={v('cinNumber') ? ['gst','pan','cin'] : ['gst','pan']} />

          <div style={{
            padding:12, borderRadius:8, fontSize:12.5,
            background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.2)',
            color:'var(--text-secondary)',
          }}>
            💡 <strong>Cross-check tip:</strong> open the relevant document on the Documents step and confirm
            the number above matches. PAN must match the PAN card upload, GST must match GST certificate, etc.
            If a field above shows <strong>Confirmed</strong>, it matched government records at submission time.
          </div>
        </div>
      );
    }

    if (sid === 'bank') {
      // bankDetails may live at app.bankDetails OR app.formData.bankDetails depending
      // on when the application was submitted (legacy data compat).
      const bd = app.bankDetails || (app.formData && app.formData.bankDetails) || {};
      return (
        <div style={{ display:'grid', gap:12 }}>
          <FieldCard label="Account Holder Name" value={bd.accountHolderName} fieldKey="bank.accountHolderName" flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          <FieldCard label="Bank Name"           value={bd.bankName}          fieldKey="bank.bankName"          flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FieldCard label="Account Number (full)" value={bd.accountNumber} fieldKey="bank.accountNumber" flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
            <FieldCard label="IFSC Code"             value={bd.ifscCode}      fieldKey="bank.ifscCode"      flags={flags} onFlag={requestFlag} onUnflag={removeFlag} mono/>
          </div>
          <div className="resp-grid-2col-equal" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <FieldCard label="Branch Name"   value={bd.branchName}      fieldKey="bank.branchName"   flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
            <FieldCard label="UPI ID"        value={bd.upiId}           fieldKey="bank.upiId"        flags={flags} onFlag={requestFlag} onUnflag={removeFlag}/>
          </div>

          {/* Live bank account verification result */}
          <KycPanel kyc={app.kyc} only={['bank']} />

          <div style={{
            padding:12, borderRadius:8, fontSize:12.5,
            background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.2)',
            color:'var(--text-secondary)',
          }}>
            🏦 <strong>Verify against cancelled cheque / bank proof on Documents step.</strong>
            Account holder name should match either the hospital's registered name or
            authorised signatory; IFSC must be a valid 11-character code.
            If bank verification above shows <strong>Confirmed</strong>, the account exists and the name on the
            account has been confirmed with the beneficiary bank.
          </div>
        </div>
      );
    }

    // ── Decision ────────────────────────────────────────────────────────
    return (
      <div>
        {/* Summary of all flags */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontWeight:800, fontSize:14, marginBottom:10 }}>
            Review Summary
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:8 }}>
            {[
              { id:'basic', label:'Basic Info' },
              { id:'docs',  label:'Documents'  },
              { id:'legal', label:'Legal/KYC'  },
              { id:'bank',  label:'Bank'       },
              { id:'agreement', label:'Agreement', custom: !agreementSignedPdfUrl(app) },
            ].map(s => {
              const n = s.id === 'agreement'
                ? (agreementSignedPdfUrl(app) ? 0 : 1)
                : flagsBySection[s.id]?.length || 0;
              const issue = n > 0;
              return (
                <div key={s.id} style={{
                  padding:'10px 12px', borderRadius:9,
                  background: issue ? 'rgba(239,68,68,0.08)' : 'rgba(0,212,170,0.06)',
                  border:`1px solid ${issue ? 'rgba(239,68,68,0.25)' : 'rgba(0,212,170,0.25)'}`,
                  textAlign:'center',
                }}>
                  <div style={{ fontSize:11, color:'var(--text-secondary)', marginBottom:2 }}>{s.label}</div>
                  <div style={{ fontSize:14, fontWeight:800, color: issue ? '#fca5a5' : '#5eead4' }}>
                    {s.id === 'agreement'
                      ? (agreementSignedPdfUrl(app) ? '✓ PDF' : '✗ Missing')
                      : (n > 0 ? `${n} issue${n>1?'s':''}` : '✓ Clean')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {flags.length > 0 && (
          <div style={{
            padding:14, borderRadius:10, marginBottom:16,
            background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.2)',
          }}>
            <div style={{ fontWeight:700, color:'#fbbf24', marginBottom:8, fontSize:13 }}>
              🚩 Flagged items ({flags.length})
            </div>
            <ul style={{ margin:0, paddingLeft:18, fontSize:12.5, color:'var(--text-primary)' }}>
              {flags.map((f, i) => (
                <li key={i} style={{ marginBottom:5 }}>
                  <strong>{f.label}:</strong>{' '}
                  <span style={{ color:'#fde68a', fontStyle:'italic' }}>{f.issue}</span>
                  {f.flaggedByName && (
                    <span style={{ color:'var(--text-muted)', fontSize:11 }}> — flagged by {f.flaggedByName}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Decision pickers */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10, marginBottom:14 }}>
          <DecisionBtn
            active={decision==='approve'}
            color="#00d4aa" bg="rgba(0,212,170,0.12)" bord="rgba(0,212,170,0.3)"
            icon={<MdCheckCircle/>} label="Approve"
            disabled={flags.length > 0}
            hint={flags.length > 0 ? 'Resolve flags first' : 'Onboard hospital'}
            onClick={() => { setDecision('approve'); }}
          />
          <DecisionBtn
            active={decision==='resubmit'}
            color="#a855f7" bg="rgba(168,85,247,0.12)" bord="rgba(168,85,247,0.3)"
            icon={<MdEmail/>} label="Request Resubmission"
            disabled={flags.length === 0}
            hint={flags.length === 0 ? 'Flag at least one issue first' : `Email ${flags.length} fix${flags.length>1?'es':''}`}
            onClick={() => { setDecision('resubmit'); }}
          />
          <DecisionBtn
            active={decision==='reject'}
            color="#f87171" bg="rgba(239,68,68,0.1)" bord="rgba(239,68,68,0.3)"
            icon={<MdClose/>} label="Reject"
            hint="Decline application"
            onClick={() => { setDecision('reject'); }}
          />
        </div>

        {decision && (
          <div>
            <div style={{ marginBottom:12 }}>
              <label style={{ display:'block', fontSize:12, color:'var(--text-secondary)', marginBottom:6, fontWeight:700 }}>
                Your name * <span style={{ fontWeight:400, color:'var(--text-muted)' }}>(recorded permanently on this decision)</span>
              </label>
              <input
                type="text"
                value={reviewerName}
                onChange={e => {
                  setReviewerName(e.target.value);
                  setAdminReviewerName(e.target.value);
                }}
                placeholder="Full name of reviewer"
                style={{
                  width:'100%', padding:'10px 12px', fontSize:13,
                  background:'var(--surface-subtle)', border:'1px solid var(--border-color-hover)',
                  borderRadius:8, color:'var(--text-primary)', fontFamily:'inherit',
                }}
              />
            </div>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginBottom:6 }}>
              {decision === 'approve'  && 'Optional internal note (will not be emailed):'}
              {decision === 'resubmit' && 'Optional message to applicant (in addition to flagged items):'}
              {decision === 'reject'   && <strong style={{ color:'#fca5a5' }}>Rejection reason (will be emailed) *</strong>}
            </div>
            <textarea
              value={decNote}
              onChange={e => setDecNote(e.target.value)}
              rows={3}
              placeholder={decision === 'reject' ? 'e.g. Required documents missing and applicant unresponsive…' : 'Optional…'}
              style={{
                width:'100%', padding:10, fontSize:13,
                background:'var(--surface-subtle)', border:'1px solid var(--border-color-hover)',
                borderRadius:8, color:'var(--text-primary)', resize:'vertical', fontFamily:'inherit',
                marginBottom:14,
              }}
            />
            <button
              disabled={submitting}
              onClick={() => submit(
                decision==='approve' ? 'approved' :
                decision==='reject'  ? 'rejected' :
                                       'needs_resubmission'
              )}
              style={{
                width:'100%', padding:'12px 18px',
                background: decision==='approve' ? 'rgba(0,212,170,0.2)'
                          : decision==='reject'  ? 'rgba(239,68,68,0.15)'
                                                 : 'rgba(168,85,247,0.18)',
                border:`1px solid ${decision==='approve' ? 'rgba(0,212,170,0.4)' : decision==='reject' ? 'rgba(239,68,68,0.4)' : 'rgba(168,85,247,0.4)'}`,
                borderRadius:10,
                color: decision==='approve' ? '#00d4aa' : decision==='reject' ? '#f87171' : '#c4b5fd',
                cursor: submitting ? 'wait' : 'pointer', fontSize:14, fontWeight:800,
                display:'flex', alignItems:'center', justifyContent:'center', gap:6,
              }}
            >
              {submitting ? '⏳ Submitting…'
                : decision==='approve' ? '✅ Approve & onboard hospital'
                : decision==='reject'  ? '❌ Reject application'
                                       : `📧 Send back with ${flags.length} flag${flags.length>1?'s':''}`}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.78)', zIndex:100,
      display:'flex', alignItems:'center', justifyContent:'center', padding:'2vh',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        width:'100%', maxWidth:880, height:'96vh',
        background:'#0d1525', border:'1px solid var(--border-color)',
        borderRadius:16, display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding:'16px 22px', borderBottom:'1px solid var(--surface-muted)',
          display:'flex', alignItems:'center', gap:14,
        }}>
          <MdLocalHospital style={{ fontSize:22, color:'#a5b4fc' }}/>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:17, fontWeight:800, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {liveApp.hospitalName}
            </div>
            <div style={{ fontSize:12, color:'var(--text-muted)', fontFamily:'monospace' }}>
              {liveApp.applicationId}
            </div>
          </div>
          {flags.length > 0 && (
            <Pill color="#fbbf24" bg="rgba(245,158,11,0.15)">
              <MdFlag size={11}/> {flags.length} flag{flags.length>1?'s':''}
            </Pill>
          )}
          <button onClick={onClose} style={{
            background:'var(--surface-subtle)', border:'1px solid var(--border-color-hover)',
            borderRadius:8, color:'#94a3b8', cursor:'pointer', padding:7, display:'flex',
          }}><MdClose size={18}/></button>
        </div>

        <div style={{
          padding:'10px 22px', borderBottom:'1px solid var(--surface-muted)',
          background:'rgba(99,102,241,0.06)', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
        }}>
          <label style={{ fontSize:12, fontWeight:700, color:'#a5b4fc', whiteSpace:'nowrap' }}>
            Reviewer name *
          </label>
          <input
            type="text"
            value={reviewerName}
            onChange={e => {
              setReviewerName(e.target.value);
              setAdminReviewerName(e.target.value);
            }}
            placeholder="Enter your name before approve / flag / reject"
            style={{
              flex:'1 1 220px', minWidth:200, padding:'8px 12px', fontSize:13,
              background:'var(--surface-subtle)', border:'1px solid rgba(99,102,241,0.35)',
              borderRadius:8, color:'var(--text-primary)', fontFamily:'inherit',
            }}
          />
          <span style={{ fontSize:11, color:'var(--text-muted)' }}>Saved on this device · stored permanently on each action</span>
        </div>

        {/* Stepper */}
        <div style={{
          padding:'12px 22px', borderBottom:'1px solid var(--surface-muted)',
          display:'flex', gap:6, overflowX:'auto',
        }}>
          {STEPS.map((s, i) => {
            const active = i === step;
            const done   = i < step;
            const sectionFlags = flagsBySection[s.id]?.length || 0;
            return (
              <button
                key={s.id}
                onClick={() => setStep(i)}
                style={{
                  flex:'1 1 auto', minWidth:115, padding:'8px 10px', borderRadius:8,
                  border:'1px solid', cursor:'pointer', display:'flex', alignItems:'center', gap:6,
                  background: active ? 'rgba(99,102,241,0.18)' : done ? 'rgba(0,212,170,0.07)' : 'transparent',
                  borderColor: active ? 'rgba(99,102,241,0.4)' : done ? 'rgba(0,212,170,0.25)' : 'var(--border-color)',
                  color: active ? '#c7d2fe' : done ? '#5eead4' : 'var(--text-secondary)',
                  fontSize:12, fontWeight:700, fontFamily:'inherit', whiteSpace:'nowrap',
                }}
              >
                <span style={{ fontSize:14 }}>{s.icon}</span>
                <span style={{ flex:1, textAlign:'left' }}>{i+1}. {s.label}</span>
                {sectionFlags > 0 && (
                  <span style={{
                    padding:'1px 6px', borderRadius:10, fontSize:10,
                    background:'rgba(239,68,68,0.18)', color:'#fca5a5',
                  }}>{sectionFlags}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>
          {renderStep()}
        </div>

        {/* Footer nav */}
        {STEPS[step].id !== 'decision' && (
          <div style={{
            padding:'12px 22px', borderTop:'1px solid var(--surface-muted)',
            display:'flex', alignItems:'center', gap:10,
          }}>
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              style={{
                padding:'9px 16px', background:'var(--surface-subtle)',
                border:'1px solid var(--border-color-hover)', borderRadius:8,
                color: step===0 ? 'var(--text-muted)' : '#cbd5e1',
                cursor: step===0 ? 'not-allowed' : 'pointer',
                fontSize:13, fontWeight:600,
                display:'flex', alignItems:'center', gap:5,
              }}
            ><MdArrowBack size={16}/>Back</button>

            <div style={{ flex:1, textAlign:'center', fontSize:12, color:'var(--text-muted)' }}>
              Step {step+1} of {STEPS.length}
            </div>

            <button
              onClick={() => setStep(s => Math.min(STEPS.length-1, s + 1))}
              style={{
                padding:'9px 18px', background:'rgba(99,102,241,0.18)',
                border:'1px solid rgba(99,102,241,0.35)', borderRadius:8,
                color:'#a5b4fc', cursor:'pointer', fontSize:13, fontWeight:700,
                display:'flex', alignItems:'center', gap:5,
              }}
            >Next<MdArrowForward size={16}/></button>
          </div>
        )}
      </div>

      {pendFlag && (
        <FlagDialog
          field={pendFlag.field}
          label={pendFlag.label}
          onCancel={() => setPendFlag(null)}
          onSubmit={confirmFlag}
        />
      )}

      {credentials && (
        <div style={{
          position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.85)',
          display:'flex', alignItems:'center', justifyContent:'center', padding:20,
        }} onClick={() => { onUpdated?.({ ...app, status:'approved', hospitalId: credentials.hospitalId }); onClose?.(); }}>
          <div onClick={e => e.stopPropagation()} style={{
            width:'100%', maxWidth:480, background:'#0d1525',
            border:'1px solid rgba(0,212,170,0.35)', borderRadius:16, padding:24,
          }}>
            <div style={{ fontSize:20, fontWeight:800, color:'#5eead4', marginBottom:8 }}>
              ✅ Hospital Approved
            </div>
            <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:18, lineHeight:1.6 }}>
              Login credentials were emailed to the hospital.
              Save a copy below in case the email is delayed.
            </div>
            <div style={{ display:'grid', gap:10, marginBottom:16 }}>
              {[
                ['Hospital ID', credentials.hospitalId],
                ['Login email', credentials.email],
                ['Password', credentials.tempPassword],
                ['Login URL', 'https://hospitals.hcen.in/login'],
              ].map(([k, v]) => (
                <div key={k} style={{
                  padding:'10px 12px', borderRadius:8,
                  background:'var(--surface-subtle)', border:'1px solid var(--border-color)',
                }}>
                  <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5 }}>{k}</div>
                  <div style={{
                    fontSize:14, fontWeight:700, color:'var(--text-primary)', marginTop:4,
                    fontFamily: k === 'Password' || k === 'Hospital ID' ? 'monospace' : 'inherit',
                    wordBreak:'break-all',
                  }}>{v}</div>
                </div>
              ))}
            </div>
            {credentials.cognitoWarning && (
              <div style={{
                fontSize:12, color:'#fbbf24', marginBottom:14, padding:'10px 12px',
                background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:8,
              }}>
                {credentials.cognitoWarning}
              </div>
            )}
            <button
              onClick={() => { onUpdated?.({ ...app, status:'approved', hospitalId: credentials.hospitalId }); onClose?.(); }}
              style={{
                width:'100%', padding:'12px', borderRadius:10, border:'none', cursor:'pointer',
                background:'linear-gradient(135deg,#00d4aa,#008080)', color:'#000', fontWeight:800, fontSize:14,
              }}
            >
              Done — Close review
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tiny decision button ───────────────────────────────────────────────
function DecisionBtn({ active, color, bg, bord, icon, label, hint, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding:'12px 14px', borderRadius:10,
        background: disabled ? 'var(--surface-subtle)' : active ? bg : 'var(--surface-subtle)',
        border:`1.5px solid ${disabled ? 'var(--surface-muted)' : active ? bord : 'var(--border-color)'}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: disabled ? 'var(--text-muted)' : active ? color : 'var(--text-secondary)',
        textAlign:'left', fontFamily:'inherit',
        transition:'all 0.15s',
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:6, fontWeight:800, fontSize:13.5, marginBottom:3 }}>
        <span style={{ fontSize:16 }}>{icon}</span>{label}
      </div>
      <div style={{ fontSize:11, opacity:0.75 }}>{hint}</div>
    </button>
  );
}
